import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { KeyboardSafeForm } from '../../components/KeyboardSafeForm';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  RequestFormDateField,
  RequestFormLabel,
  RequestFormTextField,
} from '../../components/RequestFormField';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnderlineTabs } from '../../components/UnderlineTabs';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { CompensatoryWorkPanel } from './CompensatoryWorkPanel';
import { getLeaveModuleCapabilities } from '../../config/leaveCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { LateArrivalMonthCard } from '../../components/LateArrivalMonthCard';
import { LopMonthSummary } from '../../components/LopMonthSummary';
import { getLateArrivalMonthSummary } from '../../services/lateArrivalConfigurationService';
import type { LateArrivalMonthSummary } from '../../types/lateArrivalConfiguration';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getLeaveApplications, getLeaveDashboard, applyLeave, getLeaveSessionTypes } from '../../services/leaveService';
import { getMonthlyCalendar } from '../../services/officeCalendarService';
import type {
  LeaveApplicationListItem,
  LeaveBalance,
  LeaveDurationOption,
  LeaveDurationRule,
  LeaveSessionOption,
  MonthlyLopSummary,
} from '../../types/leave';
import type { CalendarDay } from '../../types/officeCalendar';
import {
  canSeeRequestApprovals,
  leaveApprovalListTitle,
} from '../../utils/approvalRoles';
import {
  formatCalendarDate,
} from '../../utils/dateTime';
import { leaveErrorMessage } from '../../utils/leaveErrors';
import { normalizeApiError } from '../../api/apiError';
import {
  isValidLeaveDate,
  validateLeaveDateRange,
} from '../../utils/leaveFilters';
import {
  computeInclusiveLeaveDays,
  computeLeaveTotalDays,
  conflictingLeaveApplicationMessage,
  durationsAllowedForBalance,
  findLeaveDurationRule,
  formatLeaveApplicationDuration,
  formatLeaveBalanceValue,
  isQuarterDayDuration,
  leaveDurationRuleHint,
  leaveDurationRuleViolation,
  localTodayDate,
  usableLeaveBalance,
} from '../../utils/leaveSession';
import { leaveBalanceDisplayColor } from '../../utils/leaveBalance';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { officeCalendarOffDates } from '../../utils/officeCalendar';

type Props = BottomTabScreenProps<MainTabParamList, 'Leave'>;
type LeaveTab = 'My Leaves' | 'Apply Leave' | 'Comp Work';

const LEAVE_TABS: readonly LeaveTab[] = ['My Leaves', 'Apply Leave', 'Comp Work'];

function FilterIcon({ color }: { color: string }) {
  return (
    <View style={styles.filterIcon} accessibilityElementsHidden>
      <View style={[styles.filterTop, { borderBottomColor: color }]} />
      <View style={[styles.filterMid, { backgroundColor: color }]} />
      <View style={[styles.filterBottom, { backgroundColor: color }]} />
    </View>
  );
}

function LeaveApplicationRow({
  item,
  onPress,
  palette,
}: {
  item: LeaveApplicationListItem;
  onPress?: () => void;
  palette: ColorPalette;
}) {
  const leaveType = formatDisplayLabel(
    item.leaveTypeName,
    'Leave type unavailable',
  );
  const duration = formatLeaveApplicationDuration(
    item.totalDays,
    item.sessionType,
    [],
    item.sessionTypeName,
  );
  const dateRange = `${formatCalendarDate(item.leaveFromDate)} - ${formatCalendarDate(item.leaveToDate)}`;
  const content = (
    <>
      <View style={styles.rowTop}>
        <Text style={[styles.rowTitle, { color: palette.textPrimary }]}>
          {leaveType}
        </Text>
        <Text style={[styles.rowDuration, { color: palette.textPrimary }]}>
          {duration}
        </Text>
      </View>
      <View style={styles.rowBottom}>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {dateRange}
        </Text>
        <LeaveStatusBadge
          label={item.statusLabel}
          statusCode={item.statusCode}
        />
      </View>
    </>
  );

  if (!onPress) {
    return (
      <View
        accessibilityRole="text"
        accessibilityLabel={`${leaveType}, ${duration}, ${item.statusLabel}`}
        style={[styles.row, { borderBottomColor: palette.border }]}
      >
        {content}
      </View>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${leaveType}, ${duration}, ${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      {content}
    </Pressable>
  );
}

function monthRange(date: string): { fromDate: string; toDate: string } {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const prefix = date.slice(0, 7);
  return { fromDate: `${prefix}-01`, toDate: `${prefix}-${String(lastDay).padStart(2, '0')}` };
}

function ApplyLeaveForm({
  canApply,
  balances,
  durationOptions,
  rules,
  balancesLoadFailed,
  palette,
  onSuccess,
  onPolicyChanged,
}: {
  canApply: boolean;
  balances: LeaveBalance[];
  durationOptions: LeaveDurationOption[];
  rules: LeaveDurationRule[];
  balancesLoadFailed: boolean;
  palette: ColorPalette;
  onSuccess: () => void;
  /** HR Admin changed the employee's leave policy: reload balances. */
  onPolicyChanged: () => void;
}) {
  const selectableBalances = useMemo(
    () =>
      balances.filter(
        balance => balance.leaveTypeId != null && balance.leaveTypeId > 0,
      ),
    [balances],
  );
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<number | null>(
    null,
  );
  const [durationCode, setDurationCode] = useState('');
  const [sessionCode, setSessionCode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [handoverNotes, setHandoverNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const selectedBalance =
    selectableBalances.find(
      balance => balance.leaveTypeId === selectedLeaveTypeId,
    ) ?? null;
  const allowedDurations = useMemo(
    () =>
      selectedBalance
        ? durationsAllowedForBalance(durationOptions, selectedBalance, rules)
        : durationOptions,
    [durationOptions, rules, selectedBalance],
  );
  const selectedDuration =
    allowedDurations.find(option => option.durationCode === durationCode) ??
    allowedDurations[0] ??
    null;
  const quarterDuration = isQuarterDayDuration(selectedDuration, durationOptions);
  const matchedSession =
    selectedDuration?.sessions.find(
      session => session.sessionCode === sessionCode,
    ) ?? null;
  // Quarter day waits for an explicit choice. Half day keeps the first session.
  const selectedSession: LeaveSessionOption | null = quarterDuration
    ? matchedSession
    : matchedSession ?? selectedDuration?.sessions[0] ?? null;
  const resolvedPolicyId = Number(selectedBalance?.leavePolicyId);
  const effectiveToDate = selectedDuration?.isHalfDay ? fromDate : toDate;
  const offDates = useMemo(
    () => officeCalendarOffDates(calendarDays),
    [calendarDays],
  );
  const totalDays = computeLeaveTotalDays(
    fromDate.trim(),
    effectiveToDate.trim(),
    selectedSession ?? selectedDuration,
    offDates,
  );
  const inclusiveDays = computeInclusiveLeaveDays(
    fromDate.trim(),
    effectiveToDate.trim(),
  );
  const excludedOffDays =
    inclusiveDays != null && totalDays != null && !selectedDuration?.isHalfDay
      ? Math.max(0, inclusiveDays - totalDays)
      : selectedDuration?.isHalfDay && offDates.has(fromDate.trim())
        ? 1
        : 0;
  const totalDaysLabel =
    totalDays != null ? String(totalDays) : '';
  const usableBalance = usableLeaveBalance(selectedBalance);
  const availableLabel = selectedBalance?.isLossOfPay
    ? `${selectedBalance.daysTaken ?? 0} day(s) taken (${selectedBalance.leaveYear ?? ''})`
    : formatLeaveBalanceValue(
    usableBalance,
    selectedBalance?.unit,
  );
  const monthOnlyBalance = !selectedBalance?.isLossOfPay && selectedBalance?.carryForwardAllowed === false;
  const selectedRule = findLeaveDurationRule(
    rules,
    selectedBalance?.leaveTypeId,
    selectedDuration?.durationCode,
  );
  const ruleHint = leaveDurationRuleHint(selectedRule);

  useEffect(() => {
    if (!selectedDuration) {
      if (durationCode) setDurationCode('');
      if (sessionCode) setSessionCode('');
      return;
    }
    if (durationCode !== selectedDuration.durationCode) {
      setDurationCode(selectedDuration.durationCode);
    }
    const matchesSelection = selectedDuration.sessions.some(
      session => session.sessionCode === sessionCode,
    );
    if (isQuarterDayDuration(selectedDuration, durationOptions)) {
      if (sessionCode && !matchesSelection) setSessionCode('');
      return;
    }
    if (!matchesSelection) {
      setSessionCode(selectedDuration.sessions[0]?.sessionCode ?? '');
    }
  }, [durationCode, durationOptions, selectedDuration, sessionCode]);

  useEffect(() => {
    if (selectedDuration?.isHalfDay && fromDate) {
      setToDate(fromDate);
    }
  }, [fromDate, selectedDuration?.isHalfDay]);

  useEffect(() => {
    if (
      selectedLeaveTypeId != null &&
      selectableBalances.some(
        balance => balance.leaveTypeId === selectedLeaveTypeId,
      )
    ) {
      return;
    }
    setSelectedLeaveTypeId(selectableBalances[0]?.leaveTypeId ?? null);
  }, [selectableBalances, selectedLeaveTypeId]);

  useEffect(() => {
    const dates = [fromDate.trim(), effectiveToDate.trim()].filter(isValidLeaveDate);
    if (dates.length === 0) {
      setCalendarDays([]);
      setCalendarLoading(false);
      return;
    }
    const months = new Map<string, { year: number; month: number }>();
    for (const date of dates) {
      const year = Number(date.slice(0, 4));
      const month = Number(date.slice(5, 7));
      months.set(`${year}-${month}`, { year, month });
    }
    let cancelled = false;
    setCalendarLoading(true);
    Promise.all(
      [...months.values()].map(({ year, month }) =>
        getMonthlyCalendar(year, month),
      ),
    )
      .then(results => {
        if (cancelled) return;
        setCalendarDays(results.flatMap(result => result.calendarDays));
      })
      .catch(() => {
        if (cancelled) return;
        setCalendarDays([]);
      })
      .finally(() => {
        if (!cancelled) setCalendarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveToDate, fromDate]);

  const selectLeaveType = (leaveTypeId: string) => {
    const parsed = Number(leaveTypeId);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    setSelectedLeaveTypeId(parsed);
    setSubmitError(null);
  };

  const selectDuration = (next: LeaveDurationOption) => {
    if (!canApply) return;
    setDurationCode(next.durationCode);
    setSessionCode(
      isQuarterDayDuration(next, durationOptions)
        ? ''
        : next.sessions[0]?.sessionCode ?? '',
    );
    setSubmitError(null);
  };

  const selectSession = (next: LeaveSessionOption) => {
    if (!canApply || !selectedDuration?.isHalfDay) return;
    setSessionCode(next.sessionCode);
    setSubmitError(null);
  };

  const resetForm = () => {
    setSelectedLeaveTypeId(selectableBalances[0]?.leaveTypeId ?? null);
    setDurationCode(allowedDurations[0]?.durationCode ?? '');
    setSessionCode(allowedDurations[0]?.sessions[0]?.sessionCode ?? '');
    setFromDate('');
    setToDate('');
    setReason('');
    setHandoverNotes('');
    setSubmitError(null);
  };

  const submit = async () => {
    if (!canApply || submitting) return;
    if (!selectedBalance?.leaveTypeId) {
      setSubmitError('Select a leave type from your available balances.');
      return;
    }
    if (!Number.isInteger(resolvedPolicyId) || resolvedPolicyId <= 0) {
      setSubmitError(
        'The selected leave type is not linked to a leave policy. Contact HR.',
      );
      return;
    }
    if (!selectedDuration) {
      setSubmitError('Select a leave duration from the configured options.');
      return;
    }
    if (!selectedSession) {
      setSubmitError(
        quarterDuration
          ? `Select one ${formatDisplayLabel(selectedDuration.durationName)} option.`
          : 'Select a leave duration from the configured options.',
      );
      return;
    }
    const trimmedFrom = fromDate.trim();
    const trimmedTo = selectedDuration.isHalfDay ? trimmedFrom : toDate.trim();
    const rangeError = validateLeaveDateRange(trimmedFrom, trimmedTo);
    if (rangeError) {
      setSubmitError(rangeError);
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSubmitError('Reason is required.');
      return;
    }
    const trimmedHandoverNotes = handoverNotes.trim();
    if (trimmedHandoverNotes.length > 2000) {
      setSubmitError('Handover notes must be 2000 characters or fewer.');
      return;
    }
    const days = computeLeaveTotalDays(
      trimmedFrom,
      trimmedTo,
      selectedSession,
      offDates,
    );
    const apiTotalDays = computeLeaveTotalDays(
      trimmedFrom,
      trimmedTo,
      selectedSession,
    );
    if (days == null || apiTotalDays == null) {
      setSubmitError('Total days could not be calculated from the selected dates.');
      return;
    }
    if (days <= 0) {
      setSubmitError(
        'The selected dates are holidays or weekly offs; no leave days to deduct.',
      );
      return;
    }
    if (calendarLoading) {
      setSubmitError('Office Calendar is still loading. Try again in a moment.');
      return;
    }
    const leaveTypeName = formatDisplayLabel(selectedBalance.leaveTypeName);
    const durationName = formatDisplayLabel(selectedDuration.durationName);
    const today = selectedRule?.serverDate ?? localTodayDate();
    const noticeError = leaveDurationRuleViolation({
      rule: selectedRule,
      durationName,
      leaveTypeName,
      fromDate: trimmedFrom,
      today,
      sessions: [],
      monthApplications: [],
    });
    if (noticeError) {
      setSubmitError(noticeError);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (selectedRule?.maximumRequestsPerMonth != null) {
        const monthApplications = await getLeaveApplications({
          filters: monthRange(trimmedFrom),
          page: 1,
          pageSize: 100,
        });
        const limitError = leaveDurationRuleViolation({
          rule: selectedRule,
          durationName,
          leaveTypeName,
          fromDate: trimmedFrom,
          today,
          sessions: durationOptions.flatMap(option => option.sessions),
          monthApplications: monthApplications.items,
        });
        if (limitError) {
          setSubmitError(limitError);
          return;
        }
      }
      const existing = await getLeaveApplications({
        filters: { fromDate: trimmedFrom, toDate: trimmedTo },
        page: 1,
        pageSize: 100,
      });
      const conflict = conflictingLeaveApplicationMessage({
        applications: existing.items,
        sessions: durationOptions.flatMap(option => option.sessions),
        fromDate: trimmedFrom,
        toDate: trimmedTo,
        requested: selectedSession,
      });
      if (conflict) {
        setSubmitError(conflict);
        return;
      }
      await applyLeave({
        leaveTypeId: selectedBalance.leaveTypeId,
        leavePolicyId: resolvedPolicyId,
        fromDate: trimmedFrom,
        toDate: trimmedTo,
        sessionType: selectedSession.sessionCode,
        reason: trimmedReason,
        totalDays: apiTotalDays,
        handoverNotes: trimmedHandoverNotes || undefined,
      });
      resetForm();
      onSuccess();
    } catch (error) {
      setSubmitError(
        leaveErrorMessage(error, 'Unable to submit leave application.'),
      );
      const code = normalizeApiError(error).code;
      if (code === 'LEAVE_POLICY_CHANGED' || code === 'LEAVE_POLICY_NOT_ASSIGNED') {
        onPolicyChanged();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KeyboardSafeForm>
      <RequestFormSelectField
        label="Leave Type"
        required
        value={
          selectedLeaveTypeId != null ? String(selectedLeaveTypeId) : ''
        }
        options={selectableBalances.map(balance => ({
          value: String(balance.leaveTypeId),
          label: formatDisplayLabel(balance.leaveTypeName),
        }))}
        onChange={selectLeaveType}
        placeholder="Select Leave Type"
        editable={canApply && selectableBalances.length > 0}
      />
      {selectedBalance ? (
        <View
          accessibilityRole="text"
          accessibilityLabel={`${selectedBalance.isLossOfPay ? 'Days taken' : 'Available balance'} ${availableLabel}`}
          style={[
            styles.balanceOnApply,
            { borderColor: palette.border, backgroundColor: palette.surface },
          ]}
        >
          <Text style={[styles.balanceOnApplyLabel, { color: palette.textMuted }]}>
            {selectedBalance.isLossOfPay ? 'No leave balance required' : monthOnlyBalance ? 'Available this month' : 'Available balance'}
          </Text>
          <Text
            style={[
              styles.balanceOnApplyValue,
              {
                color: leaveBalanceDisplayColor(
                  usableBalance,
                  palette.primary,
                  palette.error,
                ),
              },
            ]}
          >
            {availableLabel}
          </Text>
          {monthOnlyBalance ? (
            <Text style={[styles.balanceOnApplyLabel, { color: palette.textMuted }]}>
              Unused balance does not carry forward to next month.
            </Text>
          ) : null}
        </View>
      ) : null}
      <View>
        <RequestFormLabel label="Duration" required />
        <View style={styles.optionRow}>
          {allowedDurations.map(option => {
            const selected = option.durationCode === selectedDuration?.durationCode;
            return (
              <Pressable
                key={option.durationCode}
                accessibilityRole="button"
                accessibilityLabel={formatDisplayLabel(option.durationName)}
                accessibilityState={{ selected }}
                onPress={() => selectDuration(option)}
                disabled={!canApply}
                style={[
                  styles.optionChip,
                  {
                    borderColor: selected ? palette.primary : palette.border,
                    backgroundColor: palette.surface,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.optionChipLabel,
                    { color: selected ? palette.primary : palette.textPrimary },
                  ]}
                >
                  {formatDisplayLabel(option.durationName)}
                </Text>
              </Pressable>
            );
          })}
        </View>
        {ruleHint ? (
          <Text style={[styles.ruleHint, { color: palette.textMuted }]}>
            {ruleHint}
          </Text>
        ) : null}
      </View>
      {selectedDuration?.isHalfDay && !quarterDuration ? (
        <View>
          <RequestFormLabel label="Session" required />
          <View style={styles.optionRow}>
            {selectedDuration.sessions.map(option => {
              const selected = option.sessionCode === selectedSession?.sessionCode;
              return (
                <Pressable
                  key={option.sessionCode}
                  accessibilityRole="button"
                  accessibilityLabel={formatDisplayLabel(option.sessionName)}
                  accessibilityState={{ selected }}
                  onPress={() => selectSession(option)}
                  disabled={!canApply}
                  style={[
                    styles.optionChip,
                    {
                      borderColor: selected ? palette.primary : palette.border,
                      backgroundColor: palette.surface,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.optionChipLabel,
                      {
                        color: selected ? palette.primary : palette.textPrimary,
                      },
                    ]}
                  >
                    {formatDisplayLabel(option.sessionName)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}
      <RequestFormDateField
        label={selectedDuration?.isHalfDay ? 'Date' : 'From Date'}
        required
        value={fromDate}
        onChange={value => {
          setFromDate(value);
          setSubmitError(null);
        }}
        editable={canApply}
      />
      {selectedDuration && !selectedDuration.isHalfDay ? (
        <RequestFormDateField
          label="To Date"
          required
          value={toDate}
          onChange={value => {
            setToDate(value);
            setSubmitError(null);
          }}
          editable={canApply}
        />
      ) : null}
      {quarterDuration && selectedDuration && isValidLeaveDate(fromDate.trim()) ? (
        <View>
          <RequestFormLabel
            label={formatDisplayLabel(selectedDuration.durationName)}
            required
          />
          <View style={styles.optionRow}>
            {selectedDuration.sessions.map(option => {
              const selected = option.sessionCode === selectedSession?.sessionCode;
              return (
                <Pressable
                  key={option.sessionCode}
                  accessibilityRole="button"
                  accessibilityLabel={formatDisplayLabel(option.sessionName)}
                  accessibilityState={{ selected }}
                  onPress={() => selectSession(option)}
                  disabled={!canApply}
                  style={[
                    styles.optionChip,
                    {
                      borderColor: selected ? palette.primary : palette.border,
                      backgroundColor: palette.surface,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.optionChipLabel,
                      {
                        color: selected ? palette.primary : palette.textPrimary,
                      },
                    ]}
                  >
                    {formatDisplayLabel(option.sessionName)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}
      <RequestFormTextField
        label="Total Days"
        value={totalDaysLabel}
        onChangeText={() => undefined}
        placeholder="—"
        editable={false}
      />
      {excludedOffDays > 0 ? (
        <Text style={[styles.warning, { color: palette.textMuted }]}>
          {excludedOffDays === 1
            ? '1 holiday or weekly off from the Office Calendar is excluded.'
            : `${excludedOffDays} holidays or weekly offs from the Office Calendar are excluded.`}
        </Text>
      ) : null}
      <RequestFormTextField
        label="Reason"
        required
        value={reason}
        onChangeText={value => {
          setReason(value);
          setSubmitError(null);
        }}
        placeholder="Enter reason"
        multiline
        editable={canApply}
      />
      <RequestFormTextField
        label="Handover Notes"
        optional
        value={handoverNotes}
        onChangeText={value => {
          setHandoverNotes(value);
          setSubmitError(null);
        }}
        placeholder="Enter any work handover details..."
        multiline
        editable={canApply}
      />
      {selectableBalances.length === 0 && canApply && !balancesLoadFailed ? (
        <Text style={[styles.warning, { color: palette.warning }]}>
          No leave policy is assigned to you yet. Contact HR Admin to assign
          your leave policy.
        </Text>
      ) : null}
      {durationOptions.length === 0 && canApply ? (
        <Text style={[styles.warning, { color: palette.warning }]}>
          Leave duration options were not returned from the server. Apply cannot
          continue until session types are loaded.
        </Text>
      ) : null}
      {durationOptions.length > 0 &&
      allowedDurations.length === 0 &&
      canApply ? (
        <Text style={[styles.warning, { color: palette.warning }]}>
          This leave type does not allow the configured duration options.
        </Text>
      ) : null}
      {submitError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {submitError}
        </Text>
      ) : null}
      {canApply ? (
        <AppButton
          title="Submit Request"
          onPress={submit}
          loading={submitting}
          disabled={
            submitting ||
            selectableBalances.length === 0 ||
            allowedDurations.length === 0
          }
        />
      ) : (
        <AppButton
          title="Submit Request"
          disabled
          onPress={() => undefined}
        />
      )}
    </KeyboardSafeForm>
  );
}

export function LeaveHomeScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getLeaveModuleCapabilities(auth.permissions);
  const showTeamApprovals = canSeeRequestApprovals(
    auth.roles,
    auth.permissions,
    PERMISSIONS.LEAVE_APPROVE,
  );
  const root =
    navigation.getParent<NativeStackNavigationProp<RootStackParamList>>();

  const [activeTab, setActiveTab] = useState<LeaveTab>('My Leaves');
  const [items, setItems] = useState<LeaveApplicationListItem[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [durationOptions, setDurationOptions] = useState<LeaveDurationOption[]>(
    [],
  );
  const [leaveRules, setLeaveRules] = useState<LeaveDurationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [lateArrivalSummary, setLateArrivalSummary] =
    useState<LateArrivalMonthSummary | null>(null);
  const [lopSummary, setLopSummary] = useState<MonthlyLopSummary | null>(null);

  const openDetails = useCallback(
    (item: LeaveApplicationListItem) => {
      if (!capabilities.canViewDetails) return;
      root?.navigate(routes.leaveDetails, {
        leaveApplicationId: item.leaveApplicationId,
        applicationNumber: item.applicationNumber,
        leaveTypeName: item.leaveTypeName,
        leaveFromDate: item.leaveFromDate,
        leaveToDate: item.leaveToDate,
        totalDays: item.totalDays,
        sessionType: item.sessionType,
        sessionTypeName: item.sessionTypeName,
        statusCode: item.statusCode,
        applicationDate: item.applicationDate,
      });
    },
    [capabilities.canViewDetails, root],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canViewHistory && !capabilities.canViewBalance) {
        setItems([]);
        setBalances([]);
        setLopSummary(null);
        setDurationOptions([]);
        setLeaveRules([]);
        setHistoryError(null);
        setDashboardError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const now = new Date();
        const [historyResult, dashboardResult, sessionResult, lateArrivalResult] = await Promise.allSettled([
          capabilities.canViewHistory
            ? getLeaveApplications({ page: 1, pageSize: 20 })
            : Promise.resolve({ items: [] as LeaveApplicationListItem[] }),
          capabilities.canViewBalance
            ? getLeaveDashboard()
            : Promise.resolve({
                balances: [] as LeaveBalance[],
                lopSummary: null,
                durationOptions: [] as LeaveDurationOption[],
                rules: [] as LeaveDurationRule[],
              }),
          capabilities.canViewSessionTypes
            ? getLeaveSessionTypes()
            : Promise.resolve([] as LeaveDurationOption[]),
          capabilities.canViewBalance
            ? getLateArrivalMonthSummary(now.getFullYear(), now.getMonth() + 1)
            : Promise.resolve(null),
        ]);
        setLateArrivalSummary(
          lateArrivalResult.status === 'fulfilled' ? lateArrivalResult.value : null,
        );
        if (historyResult.status === 'fulfilled') {
          setItems(historyResult.value.items);
          setHistoryError(null);
        } else {
          setItems([]);
          setHistoryError(
            leaveErrorMessage(
              historyResult.reason,
              'Unable to load leave applications.',
            ),
          );
        }

        const dashboard =
          dashboardResult.status === 'fulfilled'
            ? dashboardResult.value
            : {balances: [], lopSummary: null, durationOptions: [], rules: []};
        setBalances(dashboard.balances);
        setLopSummary(dashboard.lopSummary ?? null);
        setLeaveRules(dashboard.rules ?? []);
        setDashboardError(
          dashboardResult.status === 'rejected'
            ? leaveErrorMessage(
                dashboardResult.reason,
                'Unable to load leave balances.',
              )
            : null,
        );

        const sessions =
          sessionResult.status === 'fulfilled' ? sessionResult.value : [];
        setDurationOptions(
          (dashboard.durationOptions ?? []).length > 0
            ? dashboard.durationOptions
            : sessions,
        );
      } catch (error) {
        // Defensive fallback for errors outside the individual API promises.
        setItems([]);
        setBalances([]);
        setLopSummary(null);
        setDurationOptions([]);
        setLeaveRules([]);
        setHistoryError(
          leaveErrorMessage(error, 'Unable to load leave applications.'),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canViewBalance, capabilities.canViewHistory, capabilities.canViewSessionTypes],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const header = useMemo(
    () => (
      <ScreenHeader bleed={false}>
        <View style={styles.headerRow}>
          <Text
            accessibilityRole="header"
            style={[styles.headerTitle, { color: palette.white }]}
          >
            {activeTab === 'Apply Leave' ? 'Apply Leave' : 'Leave'}
          </Text>
          {activeTab === 'My Leaves' && capabilities.canViewHistory ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Filters"
              onPress={() => root?.navigate(routes.leaveApplications)}
              style={styles.headerAction}
            >
              <FilterIcon color={palette.white} />
            </Pressable>
          ) : (
            <View style={styles.headerAction} />
          )}
        </View>
      </ScreenHeader>
    ),
    [activeTab, capabilities.canViewHistory, palette.white, root],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.LEAVE_APPLY}
      fallback={<UnauthorizedScreen />}
    >
      {loading && items.length === 0 && !historyError ? (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          <View style={styles.center}>
            <AppLoader message="Loading leave" />
          </View>
        </AppScreen>
      ) : (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          <UnderlineTabs
            options={LEAVE_TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
          {showTeamApprovals ? (
            <View style={styles.approvalAction}>
              <AppButton
                title={leaveApprovalListTitle(auth.roles)}
                onPress={() => root?.navigate(routes.adminLeaveRequests)}
              />
            </View>
          ) : null}

            {activeTab === 'Comp Work' ? (
              <CompensatoryWorkPanel
                leaveBalances={balances}
                leaveDurations={durationOptions}
              />
            ) : activeTab === 'My Leaves' ? (
            <View style={styles.body}>
              {historyError && items.length === 0 ? (
                <ErrorState message={historyError} onRetry={() => load()} />
              ) : (
                <FlatList
                  data={items}
                  keyExtractor={item => String(item.leaveApplicationId)}
                  refreshControl={
                    <RefreshControl
                      refreshing={refreshing}
                      onRefresh={() => load(true)}
                      tintColor={palette.primary}
                    />
                  }
                  ListHeaderComponent={
                    capabilities.canViewBalance && balances.length > 0 ? (
                      <View style={styles.balanceBlock}>
                        <Text
                          style={[
                            styles.balanceTitle,
                            { color: palette.textPrimary },
                          ]}
                        >
                          Leave Balances
                        </Text>
                        {balances.map(balance => (
                          <View
                            key={`${balance.leaveTypeId ?? balance.leaveTypeName}`}
                            style={[
                              styles.balanceRow,
                              { borderBottomColor: palette.border },
                            ]}
                          >
                            <Text
                              style={[
                                styles.balanceName,
                                { color: palette.textPrimary },
                              ]}
                            >
                              {formatDisplayLabel(balance.leaveTypeName)}
                            </Text>
                            {balance.monthlyAccrual != null && balance.monthlyAccrual > 0 ? (
                              <Text style={[styles.balanceUnit, { color: palette.textMuted }]}>
                                Accrues {balance.monthlyAccrual} / month
                                {balance.carryForwardAllowed === false
                                  ? ' · use within the month'
                                  : balance.carryForwardAllowed === true
                                    ? ' · carries forward'
                                    : ''}
                              </Text>
                            ) : null}
                            <Text
                              style={[
                                styles.balanceValue,
                                {
                                  color: leaveBalanceDisplayColor(
                                    usableLeaveBalance(balance),
                                    palette.primary,
                                    palette.error,
                                  ),
                                },
                              ]}
                            >
                              {balance.isLossOfPay
                                ? `${balance.daysTaken ?? 0} day(s) taken (${balance.leaveYear ?? ''}) · no balance`
                                : formatLeaveBalanceValue(usableLeaveBalance(balance), balance.unit)}
                            </Text>
                            {balance.isLossOfPay && (balance.pending ?? 0) > 0 ? (
                              <Text style={[styles.balanceUnit, { color: palette.textMuted }]}>Pending: {balance.pending} day(s)</Text>
                            ) : null}
                            {balance.lopDays > 0 ? (
                              <Text style={[styles.balanceUnit, { color: palette.error }]}>LOP: {balance.lopDays} day(s)</Text>
                            ) : null}
                          </View>
                        ))}
                        {lopSummary ? (
                          <LopMonthSummary summary={lopSummary} palette={palette} />
                        ) : null}
                        {lateArrivalSummary ? (
                          <LateArrivalMonthCard
                            summary={lateArrivalSummary}
                            palette={palette}
                          />
                        ) : null}
                      </View>
                    ) : null
                  }
                  ListEmptyComponent={
                    <EmptyState
                      title="No leave applications"
                      message="No leave applications were found."
                      actionTitle="Retry"
                      onAction={() => load()}
                    />
                  }
                  contentContainerStyle={styles.listContent}
                  renderItem={({ item }) => (
                    <LeaveApplicationRow
                      item={item}
                      palette={palette}
                      onPress={
                        capabilities.canViewDetails
                          ? () => openDetails(item)
                          : undefined
                      }
                    />
                  )}
                />
              )}
              <View style={styles.footer}>
                {!capabilities.canApply ? (
                  <Text style={[styles.warning, { color: palette.warning }]}>
                    Applying for leave is not available in this app yet.
                  </Text>
                ) : null}
                <AppButton
                  title="Apply Leave"
                  onPress={() => setActiveTab('Apply Leave')}
                />
              </View>
            </View>
          ) : (
            <View style={styles.body}>
              {dashboardError ? (
                <Text
                  accessibilityRole="alert"
                  style={[styles.warning, styles.applyNotice, {color: palette.warning}]}
                >
                  Leave balances could not be loaded. {dashboardError}
                </Text>
              ) : null}
              <ApplyLeaveForm
                canApply={capabilities.canApply}
                balances={balances}
                durationOptions={durationOptions}
                rules={leaveRules}
                balancesLoadFailed={dashboardError != null}
                palette={palette}
                onSuccess={() => {
                  setActiveTab('My Leaves');
                  load().catch(() => undefined);
                }}
                onPolicyChanged={() => {
                  load().catch(() => undefined);
                }}
              />
            </View>
          )}
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  center: { flex: 1, justifyContent: 'center' },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    ...typography.screenTitle,
  },
  headerAction: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterIcon: {
    width: 18,
    height: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  filterTop: {
    width: 18,
    borderBottomWidth: 6,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  filterMid: {
    width: 3,
    height: 5,
    borderRadius: 1,
  },
  filterBottom: {
    width: 8,
    height: 3,
    borderRadius: 1,
  },
  body: { flex: 1 },
  approvalAction: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.md },
  balanceBlock: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  balanceTitle: {
    ...typography.sectionTitle,
    marginBottom: spacing.xs,
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  balanceName: { ...typography.body, flex: 1 },
  balanceValue: { ...typography.body, fontWeight: '700' },
  balanceUnit: { ...typography.caption, fontWeight: '500' },
  row: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
  },
  rowTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  rowBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  rowTitle: { ...typography.body, fontWeight: '700', flex: 1 },
  rowDuration: { ...typography.body, fontWeight: '600' },
  rowMeta: { ...typography.caption, flex: 1 },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  applyScreen: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xxl,
    flexGrow: 1,
  },
  applyNotice: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
  },
  form: { gap: spacing.lg },
  optionRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  optionChip: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  optionChipLabel: { ...typography.body, fontWeight: '600' },
  balanceOnApply: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: spacing.xs,
  },
  balanceOnApplyLabel: { ...typography.caption },
  balanceOnApplyValue: { ...typography.body, fontWeight: '700' },
  warning: { ...typography.caption },
  ruleHint: { ...typography.caption, marginTop: spacing.xs },
});
